import React from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faClose, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew";
import { useFormBuilderEditorContext } from "../../../formBuilderEditorContext";
import { useFormBuilderDebouncedUpdate } from "../../../hooks/debouncedUpdate/useFormBuilderDebouncedUpdate";
import { IFormBuilderSelect, TFormBuilderSelectSettings } from "../../../model/item/formBuilderSelect";
import "./formBuilderSelectSettings.scss";
import { FormBuilderPanelDivider } from "../../FormBuilderPanelDivider";
import { IFormBuilderItemOption } from "../../../model/item/formBuilderItemOption";

interface IFormBuilderSelectSettingsProps {
  item: IFormBuilderSelect;
}

export function FormBuilderSelectSettings(props: IFormBuilderSelectSettingsProps) {
  const { item } = props;

  const { t } = useTranslation();

  const { formBuilderController } = useFormBuilderEditorContext();

  const settings = item.data.settings;

  const debouncedLabel = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: settings.label,
  });

  const debouncedPlaceholder = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: settings.placeholder,
  });

  function dispatchSettings(partialNextSettings: Partial<TFormBuilderSelectSettings>) {
    formBuilderController.updateItemSettings({
      key: item.key,
      prevItemSettings: {
        type: item.data.type,
        settings: cloneDeep(settings),
      },
      nextItemSettings: {
        type: item.data.type,
        settings: {
          ...cloneDeep(settings),
          ...partialNextSettings,
        },
      },
    });
  }

  function updateOptionLabel(label: string, index: number) {
    dispatchSettings({
      options: settings.options.map((option, optionIndex) => {
        if (optionIndex === index) {
          return {
            ...option,
            label,
          };
        } else {
          return option;
        }
      }),
    });
  }

  function updateOptionValue(value: string, index: number) {
    dispatchSettings({
      options: settings.options.map((option, optionIndex) => {
        if (optionIndex === index) {
          return {
            ...option,
            value,
          };
        } else {
          return option;
        }
      }),
    });
  }

  function shiftOption(prevIndex: number, shift: -1 | 1) {
    const nextIndex = prevIndex + shift;

    if (
      prevIndex < 0 ||
      prevIndex >= settings.options.length ||
      nextIndex < 0 ||
      nextIndex >= settings.options.length
    ) {
      return;
    }

    const options = [...settings.options];
    const temp = options[prevIndex];

    options[prevIndex] = options[nextIndex];
    options[nextIndex] = temp;

    dispatchSettings({
      options,
    });
  }

  function deleteOption(index: number) {
    const updatedOptions = [...settings.options.slice(0, index), ...settings.options.slice(index + 1)];
    const updatedRequired = updatedOptions.length === 0 ? false : settings.required;

    dispatchSettings({
      options: updatedOptions,
      required: updatedRequired,
    });
  }

  function addOption() {
    dispatchSettings({
      options: [
        ...settings.options,
        {
          id: null,
          formItemId: null,
          label: "",
          value: "",
        },
      ],
    });
  }

  return (
    <>
      <Input
        label={t("Label")}
        value={debouncedLabel.state.active ? debouncedLabel.state.value : settings.label}
        onFocus={debouncedLabel.activate}
        onChange={e =>
          debouncedLabel.change(e.target.value, label => {
            dispatchSettings({ label });
          })
        }
        onBlur={() =>
          debouncedLabel.commit(label => {
            dispatchSettings({ label });
          })
        }
      />

      <Input
        label={t("Placeholder")}
        value={debouncedPlaceholder.state.active ? debouncedPlaceholder.state.value : settings.placeholder}
        onFocus={debouncedPlaceholder.activate}
        onChange={e =>
          debouncedPlaceholder.change(e.target.value, placeholder => {
            dispatchSettings({ placeholder });
          })
        }
        onBlur={() =>
          debouncedPlaceholder.commit(placeholder => {
            dispatchSettings({ placeholder });
          })
        }
      />

      <div className="form-builder-select-settings-options-title">{t("Options")}</div>

      {settings.options.map((option, index) => (
        <FormBuilderSelectOption
          key={index}
          index={index}
          option={option}
          optionsLength={settings.options.length}
          updateOptionLabel={updateOptionLabel}
          updateOptionValue={updateOptionValue}
          shiftOption={shiftOption}
          deleteOption={deleteOption}
        />
      ))}

      <Button
        className="form-builder-select-settings-add-option"
        type="text"
        size="small"
        onClick={addOption}
      >
        <FontAwesomeIcon icon={faPlus} />
        {t("Add option")}
      </Button>

      <FormBuilderPanelDivider />

      <Checkbox
        size="medium"
        label={t("Required")}
        checked={settings.required}
        onChange={e => {
          const required = e.target.checked;

          if (required !== settings.required) {
            dispatchSettings({ required });
          }
        }}
        disabled={settings.options.length === 0}
      />
    </>
  );
}

interface IFormBuilderSelectOptionProps {
  index: number;
  optionsLength: number;
  option: IFormBuilderItemOption;
  updateOptionLabel: (label: string, index: number) => void;
  updateOptionValue: (value: string, index: number) => void;
  shiftOption: (prevIndex: number, shift: -1 | 1) => void;
  deleteOption: (index: number) => void;
}

function FormBuilderSelectOption(props: IFormBuilderSelectOptionProps) {
  const { index, optionsLength, option, updateOptionLabel, updateOptionValue, shiftOption, deleteOption } = props;
  
  const { t } = useTranslation();

  const canShiftUp = index > 0;
  const canShiftDown = index < optionsLength - 1;

  const debouncedLabel = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: option.label,
  });

  const debouncedValue = useFormBuilderDebouncedUpdate<string>({
    defaultValue: "",
    value: option.value,
  });

  return (
    <div className="form-builder-select-option">
      <Input
        labelHidden
        placeholder={t("Label")}
        value={debouncedLabel.state.active ? debouncedLabel.state.value : option.label}
        onFocus={debouncedLabel.activate}
        onChange={e =>
          debouncedLabel.change(e.target.value, label => {
            updateOptionLabel(label, index);
          })
        }
        onBlur={() =>
          debouncedLabel.commit(label => {
            updateOptionLabel(label, index);
          })
        }
      />

      <Input
        labelHidden
        placeholder={t("Value")}
        value={debouncedValue.state.active ? debouncedValue.state.value : option.value}
        onFocus={debouncedValue.activate}
        onChange={e =>
          debouncedValue.change(e.target.value, value => {
            updateOptionValue(value, index);
          })
        }
        onBlur={() =>
          debouncedValue.commit(value => {
            updateOptionValue(value, index);
          })
        }
      />

      <button
        type="button"
        className="select-option-action select-option-action-shift"
        onClick={() => shiftOption(index, -1)}
        disabled={!canShiftUp}
      >
        <FontAwesomeIcon icon={faArrowUp} />
      </button>

      <button
        type="button"
        className="select-option-action select-option-action-shift"
        onClick={() => shiftOption(index, 1)}
        disabled={!canShiftDown}
      >
        <FontAwesomeIcon icon={faArrowDown} />
      </button>

      <button
        type="button"
        className="select-option-action select-option-action-delete"
        onClick={() => deleteOption(index)}
      >
        <FontAwesomeIcon icon={faClose} />
      </button>
    </div>
  );
}